import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TECHNOLOGIES } from "@constants/global.constants";

import useUserDetails from "@hooks/useUserDetails";

import { combineClassNames, getTechDisplayLabel, isDevEnv } from "@utils/appUtils";

import DragIcon from "@components/SVGs/DragIcon";
import WPHeading from "@components/WPHeading";

import CustomTooltip from "@common/styles/components/tooltip/CustomTooltip";

import {
  CHEMISTRY_SPLIT_GROUP,
  LAYOUT_CLASS_MAP,
  LAYOUT_MODES,
  RESTRICTED_TECHNOLOGY_IDS,
  SYSTEM_DESIGN_DND_MIME_TYPE,
  TECHNOLOGY_SECTIONS,
  TREATMENT_IDS,
} from "./WPTechnologyIcons.constants";

import "./WPTechnologyIcons.scss";

const IX_TECHNOLOGY_IDS = new Set([
  TECHNOLOGIES.IXSD,
  TECHNOLOGIES.IXOS,
  TECHNOLOGIES.IXD,
  TECHNOLOGIES.IXN,
  TECHNOLOGIES.IXB,
  TECHNOLOGIES.IXMB,
  TECHNOLOGIES.IXCP,
]);
const ALWAYS_DISABLED_IDS = new Set([TREATMENT_IDS.FEED]);
const DEV_ONLY_TECHNOLOGY_IDS = new Set([TREATMENT_IDS.CCRO]);
const TECHNOLOGY_FLAG_MAP = {
  [TECHNOLOGIES.UF]: "isUFSelected",
  [TECHNOLOGIES.RO]: "isROSelected",
  [TECHNOLOGIES.CCRO]: "isROSelected",
};

export const LicenseExpiredIcon = () => (
  <svg width='13' height='13' viewBox='0 0 13 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M11.7615 10.5328L6.64271 1.67344C6.56642 1.54178 6.43476 1.47656 6.30187 1.47656C6.16898 1.47656 6.03609 1.54178 5.96103 1.67344L0.842284 10.5328C0.690937 10.7961 0.880429 11.1234 1.18312 11.1234H11.4206C11.7233 11.1234 11.9128 10.7961 11.7615 10.5328ZM5.90812 5.11875C5.90812 5.06461 5.95242 5.02031 6.00656 5.02031H6.59719C6.65133 5.02031 6.69562 5.06461 6.69562 5.11875V7.38281C6.69562 7.43695 6.65133 7.48125 6.59719 7.48125H6.00656C5.95242 7.48125 5.90812 7.43695 5.90812 7.38281V5.11875ZM6.30187 9.45C6.14732 9.44684 6.00016 9.38323 5.89197 9.27281C5.78378 9.16239 5.72318 9.01396 5.72318 8.85937C5.72318 8.70479 5.78378 8.55636 5.89197 8.44594C6.00016 8.33552 6.14732 8.2719 6.30187 8.26875C6.45643 8.2719 6.60359 8.33552 6.71178 8.44594C6.81997 8.55636 6.88056 8.70479 6.88056 8.85937C6.88056 9.01396 6.81997 9.16239 6.71178 9.27281C6.60359 9.38323 6.45643 9.44684 6.30187 9.45Z'
      fill='white'
    />
  </svg>
);

export const LicenseExpiredBadge = ({ relativePositioned }) => (
  <span
    className={combineClassNames(
      "wp-tech-icon__warning-badge",
      relativePositioned && "wp-tech-icon__warning-badge--relative",
    )}
  >
    <LicenseExpiredIcon />
  </span>
);

const TechnologyIcon = forwardRef(
  (
    {
      label,
      icon,
      onClick,
      active,
      enableDrag,
      disabled,
      isLicenseExpired,
      draggable,
      onDragStart,
      onDragEnd,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      className={`wp-tech-icon ${active ? "wp-tech-icon--active" : ""} ${disabled ? "wp-tech-icon--disabled" : ""} ${draggable ? "wp-tech-icon--draggable" : ""} ${isLicenseExpired ? "wp-tech-icon--expired" : ""}`}
      onClick={disabled ? undefined : onClick}
      onDragStart={disabled ? undefined : onDragStart}
      onDragEnd={disabled ? undefined : onDragEnd}
      type='button'
      aria-pressed={!!active}
      disabled={disabled}
      draggable={draggable}
      {...rest}
    >
      {enableDrag && <DragIcon size='small' />}
      {icon && <span className='wp-tech-icon__icon'>{icon}</span>}
      {label && getTechDisplayLabel(label)}
      {isLicenseExpired && <LicenseExpiredBadge />}
    </button>
  ),
);

TechnologyIcon.displayName = "TechnologyIcon";

/**
 * Determines if a technology icon should be disabled based on selected technologies.
 * @param {string} id - The technology identifier (e.g., "uf", "ro", "ixsd").
 * @param {Object} [selectedTechnologies] - Object containing technology selection flags.
 * @param {boolean} [selectedTechnologies.isUFSelected] - Whether UF technology is selected.
 * @param {boolean} [selectedTechnologies.isROSelected] - Whether RO technology is selected.
 * @param {boolean} [selectedTechnologies.isIXSelected] - Whether IX technology is selected.
 * @returns {boolean} True if the icon should be disabled, false otherwise.
 */
const getDisabledState = (id, selectedTechnologies) => {
  if (!selectedTechnologies) return false;

  const flagKey = TECHNOLOGY_FLAG_MAP[id] ?? (IX_TECHNOLOGY_IDS.has(id) ? "isIXSelected" : null);
  return flagKey ? !selectedTechnologies[flagKey] : false;
};

const TechnologyGroup = ({
  group,
  size,
  titleKey,
  items,
  onItemClick,
  showTitle,
  selectedTechnologies,
  enableDrag,
  onItemDragStart,
}) => {
  const { t } = useTranslation();

  const handleItemDragStart = (event, { id, label }) => {
    const payload = JSON.stringify({ id, label });
    event.dataTransfer.setData(SYSTEM_DESIGN_DND_MIME_TYPE, payload);
    event.dataTransfer.effectAllowed = "copyMove";
    onItemDragStart?.(id, label, group);
  };

  return (
    <div className='wp-tech-group'>
      {showTitle && (
        <WPHeading fontNoto size={size} secondary type='default' className='wp-tech-group__title'>
          {t(titleKey)}
        </WPHeading>
      )}
      <div className='wp-tech-group__items'>
        {items.map(({ id, label, labelKey, icon, tooltipKey, disabled, isLicenseExpired }) => {
          const isDisabled = isLicenseExpired || disabled || getDisabledState(id, selectedTechnologies);
          const isDraggable = enableDrag && !isDisabled;
          const displayLabel = labelKey ? t(labelKey) : label;
          const tooltipText = isLicenseExpired
            ? `${t(tooltipKey)}: ${t("systemDesign.tooltips.licenseExpiredSuffix")}`
            : t(tooltipKey);
          return (
            <CustomTooltip text={tooltipText} key={id} placement='top'>
              <TechnologyIcon
                label={displayLabel}
                icon={icon}
                enableDrag={enableDrag}
                onClick={() => onItemClick?.(id, group)}
                disabled={isDisabled}
                isLicenseExpired={isLicenseExpired}
                draggable={isDraggable}
                onDragStart={isDraggable ? event => handleItemDragStart(event, { id, label: displayLabel }) : undefined}
              />
            </CustomTooltip>
          );
        })}
      </div>
    </div>
  );
};

const WPTechnologyIcons = ({
  layout = LAYOUT_MODES.AUTO,
  columns = 2,
  showAdditionalNodes,
  showTitle = true,
  onItemClick = () => {},
  enableDrag = false,
  onItemDragStart = () => {},
  className = "",
  size = "xSmall",
  selectedTechnologies,
  ...rest
}) => {
  const { getTechnologyLicenseStatus } = useUserDetails();

  const isIconDisabled = useCallback(
    item => ({
      ...item,
      isLicenseExpired: getTechnologyLicenseStatus(item.label) === "expired",
      disabled: ALWAYS_DISABLED_IDS.has(item.id) || (DEV_ONLY_TECHNOLOGY_IDS.has(item.id) && !isDevEnv()),
    }),
    [getTechnologyLicenseStatus],
  );

  const sideNavItems = useMemo(() => {
    let sections = TECHNOLOGY_SECTIONS.map(section => ({
      ...section,
      items: section.items
        .filter(item => {
          if (RESTRICTED_TECHNOLOGY_IDS.has(item.label)) {
            return getTechnologyLicenseStatus(item.label) !== "none";
          }
          return true;
        })
        .map(isIconDisabled),
    }));

    if (showAdditionalNodes) {
      sections = [...sections, ...CHEMISTRY_SPLIT_GROUP].map(section => ({
        ...section,
        items: section.items.map(isIconDisabled),
      }));
    }

    return sections;
  }, [showAdditionalNodes, getTechnologyLicenseStatus, isIconDisabled]);

  const layoutClass = LAYOUT_CLASS_MAP[layout] || LAYOUT_CLASS_MAP[LAYOUT_MODES.AUTO];

  const gridStyle = useMemo(() => {
    if (layout === LAYOUT_MODES.GRID && columns) {
      return { "--tech-icons-columns": columns };
    }
    return {};
  }, [layout, columns]);

  return (
    <div className={`wp-tech-icons ${layoutClass} ${className || ""}`} style={gridStyle} {...rest}>
      {sideNavItems.map(section => (
        <TechnologyGroup
          key={section.group}
          {...section}
          size={size}
          onItemClick={onItemClick}
          showTitle={showTitle}
          selectedTechnologies={selectedTechnologies}
          enableDrag={enableDrag}
          onItemDragStart={onItemDragStart}
        />
      ))}
    </div>
  );
};

export default WPTechnologyIcons;
